/**
 * QuickAssign – Schnellzuordnung von Dokumenten zu Akten, Aufgaben oder Maßnahmen
 * 
 * Kompaktes Dropdown mit Suche und KI-Vorschlägen.
 * Kann direkt aus der Dokumentenliste oder dem Zuordnungsformular verwendet werden.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  FolderOpen, CheckSquare, Briefcase, Search, Sparkles, Check,
  ChevronDown, X, Loader2, ArrowRight, User, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const TABS = [
  { key: 'akte', label: 'Akte', icon: FolderOpen, color: 'blue' },
  { key: 'aufgabe', label: 'Aufgabe', icon: CheckSquare, color: 'amber' },
  { key: 'massnahme', label: 'Maßnahme', icon: Briefcase, color: 'purple' },
];

export default function QuickAssign({ dokument, onAssigned, kiEmpfehlung, compact = false }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('akte');
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Daten
  const [klienten, setKlienten] = useState([]);
  const [aufgaben, setAufgaben] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [loading, setLoading] = useState(false);

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Daten laden beim Öffnen
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      betreuungAPI.listKlienten().catch(() => []),
      betreuungAPI.listAufgaben({ status: 'offen' }).catch(() => []),
      fetch(`${BACKEND_URL}/api/betreuung/massnahmen`).then(r => r.json()).catch(() => []),
    ]).then(([kl, auf, ma]) => {
      setKlienten(Array.isArray(kl) ? kl : []);
      setAufgaben(Array.isArray(auf) ? auf : []);
      setMassnahmen(Array.isArray(ma) ? ma : []);
    }).finally(() => setLoading(false));

    // Focus search
    setTimeout(() => searchRef.current?.focus(), 100);
  }, [open]);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // KI-Vorschläge extrahieren
  const kiKlient = kiEmpfehlung?.ki_empfehlung?.klient || kiEmpfehlung?.gefundener_klient;
  const kiAufgabe = kiEmpfehlung?.ki_empfehlung?.aufgabe;

  // Gefilterte Listen
  const q = search.toLowerCase();
  const filteredKlienten = klienten.filter(k =>
    `${k.vorname} ${k.nachname}`.toLowerCase().includes(q) ||
    (k.aktenzeichen || '').toLowerCase().includes(q)
  );
  const filteredAufgaben = aufgaben.filter(a =>
    (a.titel || '').toLowerCase().includes(q) ||
    (a.betreuter_name || '').toLowerCase().includes(q)
  );
  const filteredMassnahmen = massnahmen.filter(m =>
    (m.bezeichnung || m.typ || '').toLowerCase().includes(q) ||
    (m.klient_name || '').toLowerCase().includes(q)
  );

  // Zuordnung durchführen
  const handleAssign = async (type, target) => {
    if (!dokument?.id) return;
    setAssigning(true);
    try {
      if (type === 'akte') {
        await betreuungAPI.updateDokument(dokument.id, {
          klient_id: target.id,
          dokumententyp: 'zugeordnet',
        });
        toast.success(`Dokument → Akte: ${target.vorname} ${target.nachname}`);
      } else if (type === 'aufgabe') {
        await betreuungAPI.updateDokument(dokument.id, {
          klient_id: target.klient_id || null,
          dokumententyp: 'zugeordnet',
        });
        await betreuungAPI.createAufgabenVerlauf(target.id, {
          text: `Dokument "${dokument.titel || dokument.dateiname}" zugeordnet`,
          typ: 'dokument',
        });
        toast.success(`Dokument → Aufgabe: ${target.titel}`);
      } else if (type === 'massnahme') {
        await betreuungAPI.updateDokument(dokument.id, {
          klient_id: target.klient_id || null,
          massnahme_id: target.id,
          dokumententyp: 'zugeordnet',
        });
        toast.success(`Dokument → Maßnahme: ${target.bezeichnung || target.typ}`);
      }
      setOpen(false);
      onAssigned?.();
    } catch (e) {
      toast.error('Fehler bei der Zuordnung');
    } finally {
      setAssigning(false);
    }
  };

  if (!dokument) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant={compact ? "ghost" : "outline"}
        size={compact ? "icon" : "sm"}
        onClick={() => setOpen(!open)}
        className={compact ? "h-7 w-7" : ""}
        data-testid="quick-assign-btn"
        title="Schnellzuordnung"
      >
        {compact ? (
          <ArrowRight className="w-3.5 h-3.5" />
        ) : (
          <>
            <ArrowRight className="w-4 h-4 mr-1.5" />
            Zuordnen
            <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-80 bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden"
          data-testid="quick-assign-dropdown">
          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSearch(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all ${
                  activeTab === tab.key
                    ? `text-${tab.color}-700 border-b-2 border-${tab.color}-500 bg-${tab.color}-50`
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`qa-tab-${tab.key}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Suche */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`${TABS.find(t => t.key === activeTab)?.label} suchen...`}
                className="h-8 pl-8 text-sm"
                data-testid="qa-search"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* KI-Vorschlag */}
          {kiKlient && activeTab === 'akte' && (
            <button
              onClick={() => handleAssign('akte', kiKlient)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 transition-colors text-left border-b border-indigo-100"
              data-testid="qa-ki-suggestion"
            >
              <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-indigo-800 truncate">
                  {kiKlient.vorname} {kiKlient.nachname}
                </p>
                <p className="text-xs text-indigo-600">Zeo-Empfehlung</p>
              </div>
              <ArrowRight className="w-4 h-4 text-indigo-400 shrink-0" />
            </button>
          )}

          {/* Ergebnisliste */}
          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : activeTab === 'akte' ? (
              filteredKlienten.length > 0 ? (
                filteredKlienten.map(k => (
                  <button
                    key={k.id}
                    onClick={() => handleAssign('akte', k)}
                    disabled={assigning}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 transition-colors text-left border-b border-slate-50 last:border-0"
                    data-testid={`qa-klient-${k.id}`}
                  >
                    <User className="w-4 h-4 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{k.vorname} {k.nachname}</p>
                      {k.aktenzeichen && <p className="text-xs text-slate-400">{k.aktenzeichen}</p>}
                    </div>
                    {k.status === 'aktiv' && <Badge variant="secondary" className="text-[10px] shrink-0">aktiv</Badge>}
                  </button>
                ))
              ) : (
                <p className="text-center text-sm text-slate-400 py-6">Keine Akten gefunden</p>
              )
            ) : activeTab === 'aufgabe' ? (
              filteredAufgaben.length > 0 ? (
                filteredAufgaben.map(a => (
                  <button
                    key={a.id}
                    onClick={() => handleAssign('aufgabe', a)}
                    disabled={assigning}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-50 transition-colors text-left border-b border-slate-50 last:border-0"
                    data-testid={`qa-aufgabe-${a.id}`}
                  >
                    <CheckSquare className={`w-4 h-4 shrink-0 ${
                      a.prioritaet === 'dringend' ? 'text-red-400' : a.prioritaet === 'mittel' ? 'text-amber-400' : 'text-green-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{a.titel}</p>
                      {a.betreuter_name && <p className="text-xs text-slate-400">{a.betreuter_name}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">{a.status}</Badge>
                  </button>
                ))
              ) : (
                <p className="text-center text-sm text-slate-400 py-6">Keine offenen Aufgaben</p>
              )
            ) : activeTab === 'massnahme' ? (
              filteredMassnahmen.length > 0 ? (
                filteredMassnahmen.map(m => (
                  <button
                    key={m.id}
                    onClick={() => handleAssign('massnahme', m)}
                    disabled={assigning}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-purple-50 transition-colors text-left border-b border-slate-50 last:border-0"
                    data-testid={`qa-massnahme-${m.id}`}
                  >
                    <Briefcase className="w-4 h-4 text-purple-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{m.bezeichnung || m.typ}</p>
                      {m.klient_name && <p className="text-xs text-slate-400">{m.klient_name}</p>}
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-center text-sm text-slate-400 py-6">Keine Maßnahmen gefunden</p>
              )
            ) : null}
          </div>

          {/* Footer mit Loader */}
          {assigning && (
            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird zugeordnet...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
